import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import { FormEvent, useEffect, useState } from 'react';
import { logEvent } from '../../../utils/ga';

interface CouponHistory {
  coupon: string;
  date: string;
}

type OneReward = {
  productImageUrl?: string;
  productName?: string;
  quantity?: number;
  userSelectionRate?: number;
}

type MultipleRewards = {
  slotNumber: number;
  products: OneReward[]
}

type Reward = OneReward | MultipleRewards;

// Type guard to check if a reward is OneReward
function isOneReward(reward: Reward): reward is OneReward {
  return 'productName' in reward || 'productImageUrl' in reward;
}

const KNOWN_COUPON_CODES_1 = [
  'RINKARMA', 'GUILDWAR', '7SENASENA7', 'GOODLUCK', 'THANKYOU', 'LOVELYRUBY', 'REBIRTHBACK', 'BONVOYAGE', 'YONGSANIM', 'TREASURE',
  'WELCOMEBACK', 'THEMONTHOFSENA', 'EVANKARIN', 'DARKKNIGHTS', 'SENAHAJASENA', 'CMMAY', '7777777', 'LOVESENA', 'INFINITETOWER', 'UPDATES',
  'SENARAID', 'SENAEVENTS', 'SECRETCODE', 'MAILBOX', 'YUISSONG', 'RELEASEPET', 'MOREKEYS', 'WELCOMESENA', 'HEROSOMMON', 'SENAREGOGO',
  'SHOWMETHEMONEY', 'PDKIMJUNGKI', 'INFOCODEX', 'THEHOLYCROSS', 'FUSEGETSPECIAL', 'ADVENTURER', 'NOHOSCHRONICLE', 'VALKYRIE', 'LEGENDSRAID', 'STORYEVENT',
  'SURPRISE', 'INTOTHESENA', 'FORTAGNIA', 'JJOLJACK', 'PUKIDANCE', '777SENARE', 'SEVENVSDARK', 'HTRIBERANES', 'JULYSENAMONTH', 'LODING',
];

const KNOWN_COUPON_CODES_2 = [
  'SADENDING', 'SHININGPRISM', 'MOONLIGHTCOAST', 'BLINGBLING', 'CUTEECHO', 'ILOVEPICKUP', 'SKYGATE', 'NOMARLHERO', 'LETSDOSNEA', 'RANKUPGOGO',
  'DAILYMISSIONS', 'SENA7SENA', 'BESTGUILD', 'PEPEPOPO', 'PROFILE', 'GACHAGETCHA', 'SENAARENA', 'PETEGG', 'COMINGSOONPLATIN', 'MAYSLETTER',
  'DEVELOPERNOTES', 'POWERUPHERO', 'ARENAGOLD', 'POOKIHAPPY', 'RAREHERO', 'HOTCOSTUME', 'CHECKSTORAGE',
];

export const useSevenKnightsCouponPage = () => {
  const { mutateAsync, isPending } = useMutation<Reward[], Error, RedeemCouponParams>({
    mutationFn: redeemCoupon,
  });
  const [memberCode, setMemberCode] = useState(() => {
    return localStorage.getItem('seven-knights-last-member-code') || '';
  });
  const [couponCodes, setCouponCodes] = useState('');
  const [history, setHistory] = useState<CouponHistory[]>([]);
  const [claimedRewards, setClaimedRewards] = useState<OneReward[]>([]); // 항상 OneReward 배열로 관리
  const [memberCodeHistory, setMemberCodeHistory] = useState<string[]>([]);
  const [invalidCouponHistory, setInvalidCouponHistory] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const isFormInvalid = !memberCode || couponCodes.split('\n').filter(code => code.trim() !== '').length === 0;

  useEffect(() => {
    logEvent('coupon', 'screen_view', 'seven_knights');
  }, []);

  useEffect(() => {
    const storedMemberCodeHistory = localStorage.getItem('seven-knights-member-code-history');
    if (storedMemberCodeHistory) {
      setMemberCodeHistory(JSON.parse(storedMemberCodeHistory));
    }

    const storedInvalidCouponHistory = localStorage.getItem('seven-knights-invalid-coupon-history');
    if (storedInvalidCouponHistory) {
      setInvalidCouponHistory(JSON.parse(storedInvalidCouponHistory));
    }
  }, []);

  useEffect(() => {
    if (memberCode) {
      const storedHistory = localStorage.getItem(`seven-knights-coupon-${memberCode}`);
      if (storedHistory) {
        setHistory(JSON.parse(storedHistory));
      } else {
        setHistory([]);
      }
    } else {
      setHistory([]);
    }
  }, [memberCode]);

  const removeMemberCodeFromHistory = (codeToRemove: string) => {
    logEvent('coupon', 'member_code_history_remove', 'seven_knights');
    const updatedHistory = memberCodeHistory.filter((code) => code !== codeToRemove);
    setMemberCodeHistory(updatedHistory);
    localStorage.setItem('seven-knights-member-code-history', JSON.stringify(updatedHistory));
  };

  const selectMemberCodeFromHistory = (code: string) => {
    logEvent('coupon', 'member_code_history_select', 'seven_knights');
    setMemberCode(code);
  };

  const loadKnownCoupons = (couponList: string[], source: 'known_coupon_1' | 'known_coupon_2') => {
    logEvent('coupon', 'known_coupon_load', source);
    setCouponCodes(couponList.join('\n'));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');
    setClaimedRewards([]);
    const allEnteredCoupons = Array.from(new Set(couponCodes.split('\n').map(code => code.trim()).filter(code => code !== '')));

    logEvent('coupon', 'register_attempt', 'seven_knights');

    const existingCoupons = new Set(history.map(h => h.coupon));
    const allInvalidCoupons = new Set(invalidCouponHistory);
    const couponsToProcess = allEnteredCoupons.filter(c => !existingCoupons.has(c) && !allInvalidCoupons.has(c));

    if (allEnteredCoupons.length > 0 && couponsToProcess.length === 0) {
      logEvent('coupon', 'register_blocked_duplicate_or_invalid', 'seven_knights');
      setError('입력하신 쿠폰은 모두 등록 내역 또는 잘못된 쿠폰 내역에 존재합니다.');
      setCouponCodes('');
      return;
    }

    if (couponsToProcess.length > 50) {
      logEvent('coupon', 'register_blocked_limit_exceeded', 'seven_knights');
      setError(`한 번에 최대 50개의 쿠폰만 등록할 수 있습니다. 현재 ${couponsToProcess.length}개.`);
      return;
    }

    let processedCount = 0;
    const successfullyProcessedCoupons: CouponHistory[] = [];
    const alreadyRegisteredCoupons: CouponHistory[] = [];
    const invalidCoupons: string[] = [];
    const allNewRewards: OneReward[] = []; // 타입을 OneReward[]로 명시

    for (const coupon of couponsToProcess) {
      try {
        const rewards = await mutateAsync({
          couponCode: coupon,
          pid: memberCode,
        });

        // API 응답을 정제하여 allNewRewards에 추가
        rewards.forEach(reward => {
          if (isOneReward(reward)) {
            allNewRewards.push(reward);
          } else {
            allNewRewards.push(...reward.products);
          }
        });

        successfullyProcessedCoupons.push({
          coupon,
          date: new Date().toLocaleString(),
        });
        processedCount++;

      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message;
        if (errorMessage === '해당 쿠폰의 교환 횟수를 초과하였습니다.') {
          alreadyRegisteredCoupons.push({
            coupon,
            date: '이미 사용한 쿠폰',
          });
          continue;
        } else if (errorMessage === '잘못된 쿠폰 번호입니다. 쿠폰을 다시 확인한 후 입력해 주세요.') {
          invalidCoupons.push(coupon);
          const newInvalidCouponHistory = Array.from(new Set([...invalidCouponHistory, coupon]));
          setInvalidCouponHistory(newInvalidCouponHistory);
          localStorage.setItem('seven-knights-invalid-coupon-history', JSON.stringify(newInvalidCouponHistory));
          continue;
        } else {
          logEvent('coupon', 'register_error', 'seven_knights');
          setError(errorMessage);
          setSuccessMessage('');
          const unprocessedCoupons = couponsToProcess.filter(
            c => !successfullyProcessedCoupons.some(sc => sc.coupon === c) && !alreadyRegisteredCoupons.some(ac => ac.coupon === c) && !invalidCoupons.includes(c)
          );
          setCouponCodes(unprocessedCoupons.join('\n'));
          return;
        }
      }
    }

    const finalHistory = [...successfullyProcessedCoupons, ...alreadyRegisteredCoupons, ...history];
    setHistory(finalHistory);
    localStorage.setItem(`seven-knights-coupon-${memberCode}`, JSON.stringify(finalHistory));
    setCouponCodes('');

    if (processedCount > 0 || alreadyRegisteredCoupons.length > 0) {
      const newMemberCodeHistory = Array.from(new Set([memberCode, ...memberCodeHistory]));
      setMemberCodeHistory(newMemberCodeHistory);
      localStorage.setItem('seven-knights-member-code-history', JSON.stringify(newMemberCodeHistory));
    }

    setClaimedRewards(allNewRewards);

    if (processedCount > 0) {
      setSuccessMessage(`${processedCount}개의 쿠폰이 성공적으로 등록되었습니다.`);
      logEvent('coupon', 'register_success', 'seven_knights');
    }

    if (invalidCoupons.length > 0) {
      setError(`잘못된 쿠폰 번호: ${invalidCoupons.join(', ')}`);
    }
  };

  return {
    memberCode,
    setMemberCode,
    couponCodes,
    setCouponCodes,
    history,
    handleSubmit,
    error,
    isPending,
    isFormInvalid,
    successMessage,
    claimedRewards,
    memberCodeHistory,
    removeMemberCodeFromHistory,
    selectMemberCodeFromHistory,
    loadKnownCoupons,
    KNOWN_COUPON_CODES_1,
    KNOWN_COUPON_CODES_2,
  };
};

export type RedeemCouponParams = {
  couponCode: string;
  pid: string;
}

const url = '/api/redeem';

const redeemCoupon = async (body: RedeemCouponParams): Promise<Reward[]> => {
  const { data } = await axios.post(url, body);
  // API 응답이 단일 객체일 경우 배열로 감싸서 반환
  return Array.isArray(data) ? data : [data];
};
